import React, { useRef, useLayoutEffect } from 'react';
import renderMathInElement from 'katex/dist/contrib/auto-render';

/**
 * Renders HTML or plain text content with:
 *  - KaTeX math: $$...$$, $...$, \[...\], \(...\)
 *  - Bare LaTeX auto-wrap: 5\sqrt{3} → $5\sqrt{3}$
 *  - Markdown code blocks: ```python\n...\n``` → styled <pre><code>
 *  - Markdown inline code: `x = 1` → styled <code>
 *
 * Props:
 *   html  - HTML string content
 *   text  - Plain text content
 *   Any other props are forwarded to the wrapper <span>.
 */

const KATEX_OPTIONS = {
  delimiters: [
    { left: '$$', right: '$$', display: true },
    { left: '$', right: '$', display: false },
    { left: '\\(', right: '\\)', display: false },
    { left: '\\[', right: '\\]', display: true },
  ],
  throwOnError: false,
  output: 'html',
};

// ─── Code block preprocessing ─────────────────────────────────────────────────

function escHtml(str) {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

const BLOCK_CODE_STYLE = [
  'display:block',
  'background:#1e293b',
  'color:#e2e8f0',
  'border-radius:10px',
  'padding:14px 16px',
  'margin:8px 0',
  'overflow-x:auto',
  'font-family:ui-monospace,SFMono-Regular,Menlo,monospace',
  'font-size:0.87rem',
  'line-height:1.65',
  'white-space:pre',
  'word-break:normal',
].join(';');

const LANG_LABEL_STYLE = [
  'display:block',
  'font-size:0.62rem',
  'font-weight:700',
  'letter-spacing:1.2px',
  'text-transform:uppercase',
  'color:#94a3b8',
  'margin-bottom:8px',
  'user-select:none',
].join(';');

const INLINE_CODE_STYLE = [
  'background:rgba(99,102,241,0.13)',
  'color:#818cf8',
  'border-radius:4px',
  'padding:1px 6px',
  'font-family:ui-monospace,SFMono-Regular,Menlo,monospace',
  'font-size:0.9em',
].join(';');

/**
 * Convert markdown fenced ``` ``` ``` and inline ` ` ` code to HTML.
 * Code content is HTML-escaped so backslashes / < > won't be misread as LaTeX.
 */
function preprocessCode(str) {
  if (!str || !str.includes('`')) return str;

  // Fenced code blocks: ```lang\ncode\n```
  str = str.replace(/```([a-zA-Z0-9]*)\n?([\s\S]*?)```/g, (_, lang, code) => {
    const escaped = escHtml(code.trim());
    const label = lang
      ? `<span style="${LANG_LABEL_STYLE}">${lang}</span>`
      : '';
    return `<pre style="${BLOCK_CODE_STYLE}">${label}<code>${escaped}</code></pre>`;
  });

  // Inline code: `code` (single line only)
  str = str.replace(/`([^`\n]+)`/g, (_, code) => {
    return `<code style="${INLINE_CODE_STYLE}">${escHtml(code)}</code>`;
  });

  return str;
}

// ─── Bare LaTeX wrapping ──────────────────────────────────────────────────────

// Skip: already-delimited math, entire <pre>/<code> blocks (including their
// inner text), and standalone HTML tags. Order matters — more specific patterns first.
const SKIP_RE = /(\$\$[\s\S]*?\$\$|\$[^$\n]*?\$|\\\[[\s\S]*?\\\]|\\\([\s\S]*?\\\)|<pre[\s\S]*?<\/pre>|<code[\s\S]*?<\/code>|<[^>]*>)/g;

// Bare LaTeX: optional prefix + \command + optional {arg} groups + optional suffix
const BARE_LATEX_RE = /[^\s$<>]*\\[a-zA-Z]+(?:\{[^}]*\})*[^\s$<>]*/g;

function wrapBareLaTeX(str) {
  if (!str || !str.includes('\\')) return str;

  const parts = [];
  let lastIndex = 0;
  SKIP_RE.lastIndex = 0;

  let match;
  while ((match = SKIP_RE.exec(str)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ text: str.slice(lastIndex, match.index), process: true });
    }
    parts.push({ text: match[0], process: false });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < str.length) {
    parts.push({ text: str.slice(lastIndex), process: true });
  }

  return parts.map(({ text, process }) => {
    if (!process || !text.includes('\\')) return text;
    return text.replace(BARE_LATEX_RE, (m) => `$${m}$`);
  }).join('');
}

// ─── Combined pipeline ────────────────────────────────────────────────────────

/**
 * Unescape literal \n and \t sequences that the AI sometimes emits as
 * two-character escape sequences instead of real whitespace characters.
 * Only touches bare \n / \t that are NOT inside already-HTML-escaped content.
 */
function unescapeLiteralEscapes(str) {
  if (!str) return str;
  // Replace literal backslash-n / backslash-t with real newline / tab.
  // We use a negative lookbehind for \\ so that \\n (escaped backslash + n)
  // is left alone (though the AI rarely produces those).
  return str.replace(/\\n/g, '\n').replace(/\\t/g, '\t');
}

function processContent(raw) {
  if (!raw) return '';
  return wrapBareLaTeX(preprocessCode(unescapeLiteralEscapes(raw)));
}

// ─── Component ────────────────────────────────────────────────────────────────

const MathContent = ({ html, text, ...rest }) => {
  const ref = useRef(null);
  const content = html !== undefined ? html : text;

  useLayoutEffect(() => {
    if (!ref.current) return;
    // Always use innerHTML — preprocessCode may generate HTML even from plain text.
    ref.current.innerHTML = processContent(content ?? '');
    renderMathInElement(ref.current, KATEX_OPTIONS);
  }, [content]); // eslint-disable-line react-hooks/exhaustive-deps

  return <span ref={ref} {...rest} />;
};

export default MathContent;



